/**
 * Feedback API client — used by FeedbackModal + admin Feedback tab.
 *
 * Server contract: src/routes/feedback.ts and src/routes/admin.ts.
 */

import { apiFetch } from './api';

export type FeedbackCategory = 'bug' | 'idea' | 'other' | 'love';
export type FeedbackStatus = 'new' | 'triaged' | 'archived';

export interface SubmitFeedbackInput {
  category: FeedbackCategory;
  message: string;
  screenshotKey?: string | null;
}

export interface SubmitFeedbackResponse {
  ok: true;
  id: string;
}

export async function submitFeedback(
  input: SubmitFeedbackInput,
): Promise<SubmitFeedbackResponse> {
  return apiFetch<SubmitFeedbackResponse>('/api/feedback', {
    method: 'POST',
    json: {
      category: input.category,
      message: input.message,
      screenshot_key: input.screenshotKey ?? null,
      current_url: typeof window !== 'undefined' ? window.location.href.slice(0, 500) : null,
      viewport_w: typeof window !== 'undefined' ? window.innerWidth : null,
      viewport_h: typeof window !== 'undefined' ? window.innerHeight : null,
    },
  });
}

export interface UploadedScreenshot {
  ok: true;
  key: string;
}

export async function uploadScreenshot(file: Blob, filename = 'screenshot.png'): Promise<UploadedScreenshot> {
  const form = new FormData();
  form.append('file', file, filename);
  const res = await fetch('/api/feedback/screenshot', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const text = await res.text();
  const parsed = text ? (safeJson(text) as { ok?: true; key?: string; error?: string }) : null;
  if (!res.ok || !parsed?.key) {
    throw new Error(parsed?.error ?? `Upload failed with ${res.status}`);
  }
  return { ok: true, key: parsed.key };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Admin-only client (used by AdminPage Feedback tab)
// ---------------------------------------------------------------------------

export interface AdminFeedbackRow {
  id: string;
  user_id: string | null;
  user_email: string;
  tier_snapshot: string | null;
  category: FeedbackCategory;
  message: string;
  current_url: string | null;
  user_agent: string | null;
  viewport_w: number | null;
  viewport_h: number | null;
  screenshot_r2_key: string | null;
  linked_error_ids: string | null;
  status: FeedbackStatus;
  admin_notes: string | null;
  created_at: number;
  updated_at: number;
}

export interface AdminFeedbackList {
  total: number;
  limit: number;
  offset: number;
  entries: AdminFeedbackRow[];
}

export interface AdminLinkedError {
  id: string;
  occurred_at: number;
  method: string | null;
  path: string | null;
  status: number | null;
  error_name: string | null;
  error_message: string | null;
}

export interface AdminFeedbackDetail {
  feedback: AdminFeedbackRow;
  linked_errors: AdminLinkedError[];
}

export async function adminListFeedback(params: {
  status?: FeedbackStatus;
  category?: FeedbackCategory;
  limit?: number;
  offset?: number;
}): Promise<AdminFeedbackList> {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.category) sp.set('category', params.category);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.offset !== undefined) sp.set('offset', String(params.offset));
  const qs = sp.toString();
  return apiFetch<AdminFeedbackList>(`/api/admin/feedback${qs ? `?${qs}` : ''}`);
}

export async function adminGetFeedback(id: string): Promise<AdminFeedbackDetail> {
  return apiFetch<AdminFeedbackDetail>(`/api/admin/feedback/${id}`);
}

export async function adminUpdateFeedback(
  id: string,
  patch: { status?: FeedbackStatus; admin_notes?: string | null },
): Promise<AdminFeedbackDetail> {
  return apiFetch<AdminFeedbackDetail>(`/api/admin/feedback/${id}`, {
    method: 'PATCH',
    json: patch,
  });
}

export const CATEGORY_LABEL: Record<FeedbackCategory, { emoji: string; label: string }> = {
  bug: { emoji: '🐛', label: 'Bug' },
  idea: { emoji: '✨', label: 'Idea' },
  other: { emoji: '💬', label: 'Other' },
  love: { emoji: '❤️', label: 'Love' },
};
